<template>
  <div class="info-page">
    <div class="info-hero">
      <div class="info-hero__inner">
        <p class="info-eyebrow">Help centre</p>
        <h1 class="info-h1">Frequently asked<br><em>questions</em></h1>
        <p class="info-lead">
          Everything you need to know about Maths Eleven Plus. Can't find what you're looking for?
          <RouterLink to="/info/contact" class="info-hero-link">Contact us</RouterLink>.
        </p>
      </div>
    </div>

    <div class="info-body">
      <div class="faq-groups">
        <div v-for="group in faqGroups" :key="group.category" class="faq-group">
          <h2 class="faq-group__title">{{ group.category }}</h2>
          <div class="faq-list">
            <div
              v-for="(item, i) in group.items"
              :key="i"
              class="faq-item"
              :class="{ 'faq-item--open': openItems.has(`${group.category}-${i}`) }"
            >
              <button
                class="faq-item__q"
                @click="toggle(group.category, i)"
              >
                <span>{{ item.q }}</span>
                <svg class="faq-item__chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              <div class="faq-item__a" v-show="openItems.has(`${group.category}-${i}`)">
                <p v-for="(para, pi) in item.a" :key="pi">{{ para }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Still have questions -->
      <div class="info-cta-box">
        <h3 class="info-cta-box__title">Still have questions?</h3>
        <p class="info-cta-box__desc">Our support team typically replies within one business day.</p>
        <RouterLink to="/info/contact" class="info-cta-btn">Contact Us →</RouterLink>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive } from 'vue';
import { usePageMeta } from '@/composables/usePageMeta';
usePageMeta({ title: 'FAQ — Maths Eleven Plus', description: 'Answers to common questions about Maths Eleven Plus — plans, features, the skills check, mastery bands, and more.' });

const openItems = reactive(new Set());
function toggle(category, i) {
  const key = `${category}-${i}`;
  if (openItems.has(key)) openItems.delete(key);
  else openItems.add(key);
}

const faqGroups = [
  {
    category: 'About Maths Eleven Plus',
    items: [
      {
        q: 'What is Maths Eleven Plus?',
        a: [
          'Maths Eleven Plus is an adaptive online maths practice platform designed specifically for the UK 11+ Grammar School entrance exam. It covers all 47 topic areas across Number, Algebra, Geometry, Measurement, and Problem Solving.',
          'The platform adjusts question difficulty in real time based on your child\'s performance, so every practice session is targeted and efficient.'
        ]
      },
      {
        q: 'Is Maths Eleven Plus aligned to GL and CEM assessments?',
        a: [
          'Yes. Our curriculum is mapped to both the GL Assessment and CEM (Centre for Evaluation & Monitoring) 11+ maths frameworks, which are the two most widely used assessment providers across England.'
        ]
      },
      {
        q: 'How is this different from a workbook or a tutor?',
        a: [
          'A workbook is static — it doesn\'t know what your child has already mastered, and it can\'t adjust when they\'re struggling. A tutor is effective but expensive and often hard to schedule consistently.',
          'Maths Eleven Plus sits between the two: it\'s always available, fully personalised, and tracks progress automatically so you know exactly where to focus.'
        ]
      },
    ]
  },
  {
    category: 'Plans & Pricing',
    items: [
      {
        q: 'Is there a free plan?',
        a: [
          'Yes. The Free plan gives you access to core practice questions for one child with no time limit — there\'s nothing to cancel. It includes basic progress tracking and up to 5 lessons per day.',
        ]
      },
      {
        q: 'What\'s the difference between Pro and Family?',
        a: [
          'Pro (£9.99/month) covers 1 child and unlocks the full feature set: unlimited practice, the full Skills Check diagnostic, revision mode, detailed progress reports, and PDF export.',
          'Family (£17.99/month) covers up to 3 children with everything in Pro, plus a family dashboard and priority support.',
        ]
      },
      {
        q: 'Can I cancel at any time?',
        a: [
          'Yes, you can cancel your subscription at any time from the Subscription page in your parent dashboard. You\'ll retain access to your paid plan until the end of your current billing period, then revert to the Free plan.'
        ]
      },
      {
        q: 'Is there a free trial for Pro?',
        a: [
          'We don\'t currently offer a time-limited trial, but the Free plan gives you a genuine taste of the platform — you can start practising immediately with no card required. Upgrade when you\'re ready.'
        ]
      },
    ]
  },
  {
    category: 'Features',
    items: [
      {
        q: 'How does adaptive learning work?',
        a: [
          'After each question, the platform updates your child\'s accuracy score for that topic. The difficulty of future questions — Easy, Medium, or Hard — adjusts to match their current level.',
          'Topics are also tracked across five mastery bands (Beginner → Developing → Secure → Confident → Mastery). The system prioritises topics where your child is below "Secure" and introduces harder challenges once they reach "Confident".',
        ]
      },
      {
        q: 'What is the Skills Check?',
        a: [
          'The Skills Check is a short diagnostic session that samples questions across the full curriculum to quickly identify your child\'s strengths and gaps. It typically takes 20–30 minutes.',
          'Results are used to personalise the learning path — topics your child already knows well are fast-tracked, while weaker areas receive more focus. The Skills Check is available on Pro and Family plans.',
        ]
      },
      {
        q: 'What is revision mode?',
        a: [
          'Revision mode uses spaced repetition to schedule review sessions for topics your child has previously practised. The platform surfaces topics that are "due" for review based on how long ago they were practised and how well they performed.',
          'Revision mode is available on Pro and Family plans.',
        ]
      },
      {
        q: 'How do the mastery bands work?',
        a: [
          'Each topic is assigned a mastery band based on your child\'s accuracy: Beginner (< 35%), Developing (35–60%), Secure (60–75%), Confident (75–85%), and Mastery (85%+).',
          'The goal is to move every topic to at least "Secure" before the exam, with key topics reaching "Confident" or "Mastery".'
        ]
      },
    ]
  },
  {
    category: 'Technical & Account',
    items: [
      {
        q: 'Does it work on mobile?',
        a: [
          'Yes. Maths Eleven Plus is fully responsive and works on smartphones, tablets, and desktops. No app download is required — it runs in your browser.',
        ]
      },
      {
        q: 'How often should my child practise?',
        a: [
          'We recommend 15–30 minutes per day, 5 days a week. The daily mission pad suggests a personalised set of tasks based on what\'s due for that day. Consistency matters more than long sessions.',
        ]
      },
      {
        q: 'Can I track multiple children?',
        a: [
          'Yes. The Free and Pro plans cover 1 child. The Family plan covers up to 3 children, each with their own separate progress profile and personalised learning path.',
        ]
      },
      {
        q: 'Is my child\'s data safe?',
        a: [
          'Yes. We store only the data needed to deliver personalised learning. We do not run advertising, sell data to third parties, or use your child\'s data for any purpose other than providing the service.',
          'All data is stored securely in the UK. See our Cookie Policy for details on what we collect in the browser.',
        ]
      },
    ]
  }
];
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600;1,700&family=Jost:wght@400;500;600;700&display=swap');

.info-page { font-family: 'Jost', sans-serif; }

.info-hero { background: #0D1628; padding: 5rem 2rem 4rem; }
.info-hero__inner { max-width: 52rem; margin: 0 auto; }
.info-eyebrow { font-size: 0.8rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #B8902A; margin-bottom: 1rem; }
.info-h1 { font-family: 'Cormorant Garamond', serif; font-size: clamp(2.25rem, 5vw, 3.25rem); font-weight: 700; color: #fff; line-height: 1.15; margin-bottom: 1.25rem; }
.info-h1 em { color: #B8902A; font-style: italic; }
.info-lead { font-size: 1.05rem; color: rgba(255,255,255,0.65); max-width: 44rem; line-height: 1.7; }
.info-hero-link { color: #B8902A; text-decoration: underline; }

.info-body { max-width: 52rem; margin: 0 auto; padding: 3rem 2rem 4rem; }

/* FAQ groups */
.faq-groups { display: flex; flex-direction: column; gap: 2.5rem; margin-bottom: 3rem; }
.faq-group__title { font-family: 'Cormorant Garamond', serif; font-size: 1.5rem; font-weight: 700; color: #0D1628; margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 1px solid #e5e7eb; }

.faq-list { display: flex; flex-direction: column; gap: 0; }
.faq-item { border-bottom: 1px solid #e5e7eb; }
.faq-item__q {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Jost', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
  text-align: left;
  transition: color 0.15s;
}
.faq-item__q:hover { color: #B8902A; }
.faq-item--open .faq-item__q { color: #B8902A; }
.faq-item__chevron { flex-shrink: 0; transition: transform 0.2s; }
.faq-item--open .faq-item__chevron { transform: rotate(180deg); }
.faq-item__a { padding: 0 0 1rem; }
.faq-item__a p { font-size: 0.9rem; line-height: 1.75; color: #4b5563; margin-bottom: 0.5rem; }
.faq-item__a p:last-child { margin-bottom: 0; }

/* CTA box */
.info-cta-box { background: #0D1628; border-radius: 1.25rem; padding: 2.5rem; text-align: center; }
.info-cta-box__title { font-family: 'Cormorant Garamond', serif; font-size: 1.75rem; font-weight: 700; color: #fff; margin-bottom: 0.5rem; }
.info-cta-box__desc { color: rgba(255,255,255,0.6); font-size: 0.95rem; margin-bottom: 1.5rem; }
.info-cta-btn { display: inline-block; background: linear-gradient(135deg, #B8902A, #d4a843); color: #fff; font-weight: 700; font-size: 0.95rem; padding: 0.75rem 2rem; border-radius: 0.5rem; text-decoration: none; transition: opacity 0.15s; }
.info-cta-btn:hover { opacity: 0.88; }
</style>
